import React, { useState, useEffect, useRef } from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { Button, Modal, Badge, Card, Form } from 'react-bootstrap';
import './PaymentPortal.css';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { FaSignOutAlt, FaBoxOpen, FaSearch } from 'react-icons/fa';
// Assuming BASE_URL is sufficient, but we often need BACKEND_URL for file paths
import BASE_URL, { BACKEND_URL } from '../config';

// --- START: REUSABLE UTILITY FUNCTION (Extracted & Adapted) ---

// Axios instance with Bearer token
const authAxios = axios.create({
  baseURL: BASE_URL,
});

authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ⭐ NEW REUSABLE FUNCTION: Collection Attachment Upload ⭐
const uploadCollectionAttachment = async (
  collectionId,
  file,
  notes,
  paymentMethod
) => {
  if (!file) return null;
  const attachableId = Number(collectionId);
  if (isNaN(attachableId) || attachableId <= 0) return null;

  const formData = new FormData();
  // Get user ID from local storage (assuming the current user created this collection)
  const userId = Number(localStorage.getItem('userId') || '0');

  // 1. Determine the API 'type' based on the selected payment method and file content
  let type = 'order_document';
  if (paymentMethod === 'cheque') {
    type = 'cheque_photo'; // Specific type for cheques
  } else if (file.type.startsWith('image/')) {
    type = 'pod_photo'; // Use POD photo for generic image/bank transfer slip image
  } else {
    type = 'order_document';
  }

  // 2. Append metadata using constants from API docs
  formData.append('attachable_type', 'collection_created'); // Specific API type
  formData.append('attachable_id', attachableId);
  formData.append('uploaded_by_id', userId);
  formData.append('remarks', notes || '');
  formData.append('type', type);

  // 3. Append the actual File object
  formData.append('file', file);

  try {
    console.log(
      'Uploading collection attachment. ID:',
      attachableId,
      'File:',
      file.name,
      'Type:',
      type
    );
    const res = await authAxios.post('/attachments', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  } catch (err) {
    console.error(
      'Collection attachment upload failed:',
      err.response?.data || err
    );
    return null;
  }
};

// --- END: REUSABLE UTILITY FUNCTION ---

const PaymentPortal = () => {
  const [show, setShow] = useState(false);
  const [activeForm, setActiveForm] = useState('formA');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');

  // 🔹 User information state
  const [userInfo, setUserInfo] = useState({ name: '', branches: [] });

  // 🔹 Navigation for logout
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    branchId: '',
    branchName: '',
    address: '',
    phone: '',
    amount: '',
    notes: '',
    attachment: null, // File object for upload
    method: '', // Crucial for attachment type logic
  });

  const [requests, setRequests] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [collections, setCollections] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersCurrentPage, setCustomersCurrentPage] = useState(1);
  const [customersLastPage, setCustomersLastPage] = useState(1);
  const customersFetchingRef = useRef(false);

  // Helper function to get first day of current month in DD-MM-YYYY format
  const getFirstDayOfMonth = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `01-${month}-${year}`;
  };

  // Helper function to get last day of current month in DD-MM-YYYY format
  const getLastDayOfMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    return `${String(lastDay).padStart(2, '0')}-${String(month).padStart(
      2,
      '0'
    )}-${year}`;
  };

  // Helper function to convert YYYY-MM-DD to DD-MM-YYYY
  const convertToDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  // Helper function to convert DD-MM-YYYY to YYYY-MM-DD (for date input)
  const convertToYYYYMMDD = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3 && parts[0].length === 2) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  // Helper function to capitalize first letter of a string
  const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const [historyData, setHistoryData] = useState([]);
  const [collectedPayments, setCollectedPayments] = useState([]);
  const [verifiedPayments, setVerifiedPayments] = useState([]);
  const [historyFilter, setHistoryFilter] = useState({
    from: convertToYYYYMMDD(getFirstDayOfMonth()),
    to: convertToYYYYMMDD(getLastDayOfMonth()),
  });
  const [summary, setSummary] = useState({});

  // --- Logout Handler ---
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    navigate('/');
  };

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Get user information and branches from API
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        // Get user ID from localStorage
        const userId = localStorage.getItem('userId');
        if (!userId) {
          // Fallback to localStorage if no userId
          const userName = localStorage.getItem('userName') || 'Unknown User';
          const branchName =
            localStorage.getItem('branchName') || 'Unknown Branch';
          setUserInfo({
            name: userName,
            branches: branchName !== 'Unknown Branch' ? [branchName] : [],
          });
          return;
        }

        // Fetch user details from API
        const res = await authAxios.get(`/users/${userId}`);
        const userData = res.data?.data || res.data || {};

        // Extract showrooms/branches
        const userBranches = Array.isArray(userData.showrooms)
          ? userData.showrooms.map((s) => s.name || s)
          : [];

        setUserInfo({
          name:
            userData.name || localStorage.getItem('userName') || 'Unknown User',
          branches: userBranches,
        });
      } catch (err) {
        console.error('Failed to fetch user info:', err);
        // Fallback to localStorage
        const userName = localStorage.getItem('userName') || 'Unknown User';
        const branchName =
          localStorage.getItem('branchName') || 'Unknown Branch';
        setUserInfo({
          name: userName,
          branches: branchName !== 'Unknown Branch' ? [branchName] : [],
        });
      }
    };

    fetchUserInfo();
  }, []);

  // Helper to get customer name by ID
  const getCustomerName = (customerId) => {
    const customer = customers.find(
      (c) => c.id.toString() === customerId.toString()
    );
    return customer ? customer.name : 'Unknown Customer';
  };

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Fetch dashboard summary
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await authAxios.get('/dashboard/payment-summary');
        // Extract data from response: { success: true, data: { pending_transfers, total_requests, odoo_payment_requests, active_drivers } }
        const summaryData = res.data?.data || res.data || {};
        setSummary(summaryData);
      } catch (err) {
        console.error('Failed to fetch payment summary:', err);
        setSummary({});
      }
    };
    fetchSummary();
  }, []);

  // Helper to get status badge color variant
  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'created':
        return 'warning';
      case 'assigned':
        return 'primary';
      case 'received':
      case 'collected':
      case 'submitted': // Added for payments
        return 'info';
      case 'verified':
        return 'success';
      case 'rejected':
      case 'cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  };
  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Fetch payment history
  const fetchHistory = async (fromDate = null, toDate = null) => {
    try {
      const params = {};

      // Helper to ensure date is in YYYY-MM-DD format
      const formatDateForAPI = (dateStr) => {
        if (!dateStr) return null;
        // If already in YYYY-MM-DD format, return as is
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateStr;
        }
        // If in DD-MM-YYYY format, convert to YYYY-MM-DD
        if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
          return convertToYYYYMMDD(dateStr);
        }
        return dateStr;
      };

      // Helper to get first day of month in YYYY-MM-DD format
      const getFirstDayOfMonthYYYYMMDD = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}-01`;
      };

      // Helper to get last day of month in YYYY-MM-DD format
      const getLastDayOfMonthYYYYMMDD = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        const lastDay = new Date(year, month, 0).getDate();
        return `${year}-${String(month).padStart(2, '0')}-${String(
          lastDay
        ).padStart(2, '0')}`;
      };

      // Use provided dates or default to current month
      let from = fromDate || historyFilter.from || getFirstDayOfMonthYYYYMMDD();
      let to = toDate || historyFilter.to || getLastDayOfMonthYYYYMMDD();

      // Convert to YYYY-MM-DD format for API
      from = formatDateForAPI(from) || getFirstDayOfMonthYYYYMMDD();
      to = formatDateForAPI(to) || getLastDayOfMonthYYYYMMDD();

      params.start_date = from;
      params.end_date = to;

      console.log('Fetching payment history with dates:', {
        start_date: from,
        end_date: to,
      });

      const [paymentsRes, collectionsRes] = await Promise.all([
        authAxios.get('/payments', { params }),
        authAxios.get('/collections', { params }),
      ]);

      const paymentsData = paymentsRes.data?.data || paymentsRes.data || [];
      const formattedPayments = paymentsData.map((p) => ({
        id: `payment-${p.id}`,
        uuid: p.uuid,
        customerId: p.customerId,
        customerName: p.customerName,
        branchId: p.branchId,
        branchName: p.branchName,
        amount: p.amount,
        status: p.status,
        driverId: p.driver?.id,
        driverName: p.driver?.name,
        method: p.method,
        created_at: p.created_at,
        type: 'Payment',
      }));

      const collectionsRawData =
        collectionsRes.data?.data?.data ||
        collectionsRes.data?.data ||
        collectionsRes.data ||
        [];
      const formattedCollections = collectionsRawData.map((c) => ({
        id: `collection-${c.id}`,
        uuid: c.uuid,
        customerId: c.customer_id,
        customerName: c.customer_name || getCustomerName(c.customer_id),
        branchId: c.showroom_id,
        branchName: c.showroom?.name,
        amount: c.amount,
        status: c.status,
        driverId: c.driver_id,
        driverName: c.driver?.name,
        method: c.method,
        created_at: c.created_at,
        type: 'Collection',
      }));

      const combinedData = [...formattedPayments, ...formattedCollections];
      combinedData.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setHistoryData(combinedData);
    } catch (err) {
      console.error('Failed to fetch payment history:', err);
      setHistoryData([]);
    }
  };

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Fetch collected payments
  const fetchCollectedPayments = async () => {
    try {
      const res = await authAxios.get('/payments', {
        params: { status: 'submitted' },
      });

      const paymentsData = res.data?.data || res.data || [];

      const formatted = paymentsData.map((p) => ({
        id: p.id,
        uuid: p.uuid,
        customerId: p.customerId,
        customerName: p.customerName,
        branchId: p.branchId,
        branchName: p.branchName,
        amount: p.amount,
        status: p.status,
        driverId: p.driver?.id,
        driverName: p.driver?.name,
        method: p.method,
        created_at: p.created_at,
      }));

      setCollectedPayments(formatted);
    } catch (err) {
      console.error('Failed to fetch collected payments:', err);
      setCollectedPayments([]);
    }
  };

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Fetch verified payments
  const fetchVerifiedPayments = async () => {
    try {
      const res = await authAxios.get('/payments', {
        params: { status: 'verified' },
      });

      const paymentsData = res.data?.data || res.data || [];

      const formatted = paymentsData.map((p) => ({
        id: p.id,
        uuid: p.uuid,
        customerId: p.customerId,
        customerName: p.customerName,
        branchId: p.branchId,
        branchName: p.branchName,
        amount: p.amount,
        status: p.status,
        driverId: p.driver?.id,
        driverName: p.driver?.name,
        method: p.method,
        created_at: p.created_at,
      }));

      setVerifiedPayments(formatted);
    } catch (err) {
      console.error('Failed to fetch verified payments:', err);
      setVerifiedPayments([]);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchHistory();
    fetchCollectedPayments();
    fetchVerifiedPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === HANDLER FUNCTIONS ===
  const handleCustomerChange = (e) => {
    const customerId = e.target.value;
    const customer = customers.find(
      (c) => c.id.toString() === customerId.toString()
    );
    if (customer) {
      setFormData((prev) => ({
        ...prev,
        customerId: customer.id,
        customerName: customer.name,
        address: customer.address || '',
        phone: customer.phone || '',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        customerId: '',
        customerName: '',
        address: '',
        phone: '',
      }));
    }
  };

  const handleBranchChange = (e) => {
    const branchId = e.target.value;
    const branch = branches.find(
      (b) => b.id.toString() === branchId.toString()
    );
    setFormData((prev) => ({
      ...prev,
      branchId: branch ? branch.id : '',
      branchName: branch ? branch.name : '',
    }));
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'attachment') {
      setFormData((prev) => ({ ...prev, attachment: files[0] }));
    } else {
      // Handles all non-file inputs, including payment 'method'
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleHistoryDateChange = (e) => {
    const { name, value } = e.target;
    setHistoryFilter((prev) => ({ ...prev, [name]: value }));
  };

  // === FETCH FUNCTIONS ===
  // Fetch a single page of customers
  const fetchCustomersPage = async (page, perPage = 20) => {
    const res = await authAxios.get(`/customers?page=${page}`, {
      params: { per_page: perPage },
    });

    const responseData = res.data?.data;
    const customersData = Array.isArray(responseData?.data)
      ? responseData.data
      : Array.isArray(responseData)
        ? responseData
        : [];

    // Extract pagination metadata
    const totalCount = responseData?.total;
    const lastPageNum =
      responseData?.last_page ||
      responseData?.meta?.last_page ||
      res.data?.last_page;
    const currentPageNum =
      responseData?.current_page ||
      responseData?.meta?.current_page ||
      res.data?.current_page ||
      page;

    return {
      customersData,
      totalCount,
      lastPageNum,
      currentPageNum,
      hasMore: lastPageNum
        ? page < lastPageNum
        : customersData.length === perPage,
    };
  };

  // Fetch all customers page by page until last page
  const fetchAllCustomersSequentially = async () => {
    // Prevent multiple simultaneous calls
    if (customersFetchingRef.current) {
      return;
    }
    customersFetchingRef.current = true;
    setCustomersLoading(true);
    setCustomers([]);
    setCustomersCurrentPage(1);
    const perPage = 20;

    try {
      let allCustomersList = [];
      let page = 1;
      let lastPageNum = null;
      let totalCount = 0;

      // Fetch first page to get metadata
      const firstPageData = await fetchCustomersPage(page, perPage);
      allCustomersList = [...firstPageData.customersData];
      lastPageNum = firstPageData.lastPageNum;
      totalCount = firstPageData.totalCount || allCustomersList.length;

      // Update state with first page data
      setCustomers(allCustomersList);
      setCustomersCurrentPage(page);
      if (lastPageNum) {
        setCustomersLastPage(lastPageNum);
      }

      // If we have last_page info, fetch remaining pages
      if (lastPageNum && lastPageNum > 1) {
        for (page = 2; page <= lastPageNum; page++) {
          try {
            const pageData = await fetchCustomersPage(page, perPage);
            if (pageData.customersData.length === 0) {
              break;
            }
            allCustomersList = [...allCustomersList, ...pageData.customersData];
            setCustomers([...allCustomersList]);
            setCustomersCurrentPage(page);
          } catch (err) {
            console.error(`Error fetching customers page ${page}:`, err);
            break;
          }
        }
      } else if (firstPageData.hasMore && !lastPageNum) {
        // If we don't have last_page info, continue fetching until we get an empty page
        page = 2;
        while (true) {
          try {
            const pageData = await fetchCustomersPage(page, perPage);
            if (pageData.customersData.length === 0) {
              break;
            }
            allCustomersList = [...allCustomersList, ...pageData.customersData];
            setCustomers([...allCustomersList]);
            setCustomersCurrentPage(page);

            // If we got fewer items than per_page, it's likely the last page
            if (pageData.customersData.length < perPage) {
              break;
            }
            page++;
          } catch (err) {
            console.error(`Error fetching customers page ${page}:`, err);
            break;
          }
        }
      }

      setCustomersLastPage(page);
      console.log(
        `Finished fetching all customers. Total: ${allCustomersList.length}`
      );
    } catch (err) {
      console.error('Error fetching all customers:', err);
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
      customersFetchingRef.current = false;
    }
  };

  // Keep fetchAllPages for showrooms (branches) - can be updated later if needed
  const fetchAllPages = async (endpoint) => {
    let page = 1;
    let allData = [];
    while (true) {
      try {
        const res = await authAxios.get(`${endpoint}?page=${page}`);
        const dataArray = res.data?.data?.data || [];
        allData = [...allData, ...dataArray];
        if (!res.data?.data?.next_page_url) break;
        page++;
      } catch (err) {
        console.error(`Error fetching ${endpoint} page ${page}:`, err);
        break;
      }
    }
    return allData;
  };

  // Fetch all payments
  useEffect(() => {
    const fetchAllPayments = async () => {
      try {
        const res = await authAxios.get('/payments', {});
        const allPayments = res.data?.data || res.data || [];

        const formatted = allPayments.map((p) => ({
          id: p.id,
          uuid: p.uuid,
          customerId: p.customerId,
          customerName: p.customerName,
          branchId: p.branchId,
          branchName: p.branchName,
          amount: p.amount,
          status: p.status, // Keep original status casing
          driverId: p.driver?.id,
          driverName: p.driver?.name,
          method: p.method,
          created_at: p.created_at,
          // Details like notes/attachments will be fetched on-demand
        }));

        setRequests(formatted);
      } catch (err) {
        console.error('Error fetching all payments:', err);
      }
    };

    fetchAllPayments();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch customers using sequential page fetching
      await fetchAllCustomersSequentially();

      // Fetch showrooms (branches) - can be updated later if needed
      const allShowrooms = await fetchAllPages('/showrooms');
      setBranches(allShowrooms);

      try {
        const resCollections = await authAxios.get('/collections', {});
        setCollections(resCollections.data?.data || []);
      } catch (err) {
        console.error('Error fetching collections:', err);
      }
    };

    fetchData();
  }, []);

  // === FORM SUBMISSION (UPDATED) ===
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (
      !formData.customerId ||
      !formData.amount ||
      !formData.branchId ||
      !formData.method
    ) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      const payload = {
        customer_id: formData.customerId,
        showroom_id: formData.branchId,
        amount: parseFloat(formData.amount),
        notes: formData.notes || '',
        status: 'created',
        method: formData.method,
      };

      // 1. Create collection
      const collectionRes = await authAxios.post('/collections', payload);
      const collectionId = collectionRes.data?.data?.id;

      // 2. Upload attachment using the new reusable function
      if (formData.attachment && collectionId) {
        // Now calling the external, clean upload function
        await uploadCollectionAttachment(
          collectionId,
          formData.attachment,
          formData.notes,
          formData.method
        );
      }

      // Update local state
      const newCollection = {
        id: collectionId,
        uuid: collectionRes.data.data.uuid || `COL-${collections.length + 1}`,
        customerId: formData.customerId,
        customerName: formData.customerName,
        branchId: formData.branchId,
        branchName: formData.branchName,
        address: formData.address,
        phone: formData.phone,
        amount: formData.amount,
        notes: formData.notes,
        status: 'Pending',
        created_at:
          collectionRes.data.data.created_at || new Date().toISOString(),
      };

      setCollections((prev) => [...prev, newCollection]);

      // Reset form
      setFormData({
        customerId: '',
        customerName: '',
        branchId: '',
        branchName: '',
        address: '',
        phone: '',
        amount: '',
        notes: '',
        attachment: null,
        method: '',
      });

      // Switch to Pending tab
      setActiveForm('formB');
      alert('Collection request created successfully!');
    } catch (err) {
      console.error(err.response || err);
      alert(err.response?.data?.error || 'Error creating collection request.');
    }
  };

  // === MODAL HANDLERS (UPDATED) ===
  const handleOpenModal = async (request, type) => {
    // The request object from the list has a prefixed ID like "payment-123" or "collection-456"
    const [itemType, realId] = request.id.toString().split('-');

    setSelectedRequest(request); // Set basic data immediately
    setModalType(type);
    setShow(true);

    if (type === 'assign') {
      // This is the "Approve" flow
      setSelectedDriver(request.driverName || '');
      setSelectedMethod(request.method || '');
    } else if (type === 'view' && realId) {
      // For 'view', fetch the full details including notes and attachments
      try {
        let endpoint = '';
        if (itemType === 'payment') {
          endpoint = `/payments/${realId}`;
        } else if (itemType === 'collection') {
          endpoint = `/collections/${realId}`;
        } else {
          console.error('Unknown item type for view modal:', itemType);
          return;
        }

        console.log(`Fetching full details from endpoint: ${endpoint}`);
        const res = await authAxios.get(endpoint);
        const detailedItem = res.data?.data || res.data;
        // --- DEBUG: Log the detailed payment data from the API ---
        console.log('Raw detailed data:', detailedItem);
        // ---------------------------------------------------------

        // Normalize the data to match the expected structure
        const normalizedItem = {
          ...request, // Keep basic info from the list
          ...detailedItem, // Overwrite with detailed info
          // Ensure core fields are consistent
          uuid: detailedItem.uuid || request.uuid,
          customerName:
            detailedItem.customer?.name ||
            detailedItem.customerName ||
            request.customerName,
          branchName:
            detailedItem.showroom?.name ||
            detailedItem.branchName ||
            request.branchName,
          driverName:
            detailedItem.driver?.name ||
            detailedItem.driverName ||
            request.driverName,
          notes: detailedItem.notes || request.notes,
          attachments:
            detailedItem.attachments || request.attachments || [],
        };

        setSelectedRequest(normalizedItem); // Update the state with the normalized data
      } catch (err) {
        console.error('Failed to fetch details:', err);
      }
    }
  };

  const handleClose = () => {
    setSelectedRequest(null);
    setModalType(null);
    setSelectedDriver('');
    setSelectedMethod('');
    setShow(false);
  };

 const handleDeleteItem = async (itemId, itemType) => {
    if (!window.confirm('Are you sure you want to delete this record? This action cannot be undone.')) return;

    try {
      const [type, realId] = itemId.toString().split('-');
      const endpoint = type === 'payment' ? `/payments/${realId}` : `/collections/${realId}`;
      await authAxios.delete(endpoint);
      setHistoryData(prev => prev.filter(item => item.id !== itemId));
      alert('Deleted successfully.');
    } catch (err) {
      console.error('Error deleting record:', err.response || err);
      alert(err.response?.data?.message || 'Failed to delete record.');
    }
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;

    // ID is prefixed, e.g., "payment-123"
    const [itemType, realId] = selectedRequest.id.toString().split('-');

    if (itemType !== 'payment' || !realId) {
      alert('Approval is only possible for payment records.');
      return;
    }

    try {
      // No payload is needed as the action is implicit in the URL for approval
      const res = await authAxios.patch(
        `/payments/${realId}/approve`
      );

      if (res.status >= 200 && res.status < 300) {
        // More robust success check for 2xx statuses
        alert('Payment approved successfully!');

        // The response from the approve endpoint should contain the updated payment.
        const updatedPayment = res.data.data;
        console.log('Updated payment data from API:', updatedPayment);

        // Update the main requests list to reflect the new status
        setRequests((prev) =>
          prev.map((req) =>
            req.id === selectedRequest.id
              ? { ...req, ...updatedPayment } // Update all fields based on backend response
              : req
          )
        );

        // Refresh collected and verified payments lists
        fetchCollectedPayments();
        fetchVerifiedPayments();

        handleClose(); // Close the modal
      }
    } catch (err) {
      console.error('Error approving request:', err.response || err);
      alert(
        err.response?.data?.message ||
        'Failed to approve request. Please check console for details.'
      );
    }
  };

  const pendingRequests = requests.filter(
    (r) => r.status?.toLowerCase() === 'verified'
  );
  const collectedRequests = requests.filter((r) => r.status === 'reconciled');
  const filteredHistoryRequests = requests.filter((req) => {
    if (!historyFilter.from && !historyFilter.to) return true;
    const reqDate = new Date(req.created_at);
    if (historyFilter.from && reqDate < new Date(historyFilter.from))
      return false;
    if (historyFilter.to && reqDate > new Date(historyFilter.to)) return false;
    return true;
  });

  // Ensure body can scroll when PaymentPortal is mounted
  useEffect(() => {
    // Override AdminLayout.css which sets overflow: hidden
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyHeight = document.body.style.height;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalHtmlHeight = document.documentElement.style.height;
    const originalRootOverflow =
      document.getElementById('root')?.style.overflow;
    const originalRootHeight = document.getElementById('root')?.style.height;

    // Force enable scrolling
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.body.style.maxHeight = 'none';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.documentElement.style.maxHeight = 'none';

    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.overflow = 'auto';
      rootElement.style.height = 'auto';
      rootElement.style.maxHeight = 'none';
    }

    return () => {
      // Reset on unmount
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.height = originalBodyHeight;
      document.body.style.maxHeight = '';
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.documentElement.style.height = originalHtmlHeight;
      document.documentElement.style.maxHeight = '';
      if (rootElement) {
        rootElement.style.overflow = originalRootOverflow || '';
        rootElement.style.height = originalRootHeight || '';
        rootElement.style.maxHeight = '';
      }
    };
  }, []);

  return (
    <div
      style={{
        background: '#f0f5ff',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Abstract Background Shapes */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        {/* Large abstract shapes */}
        <div
          style={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '-100px',
            left: '-50px',
            animation: 'float 20s infinite ease-in-out',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'rgba(186, 230, 253, 0.5)',
            top: '20%',
            right: '-80px',
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '3s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(224, 242, 254, 0.6)',
            bottom: '10%',
            left: '10%',
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '6s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '180px',
            height: '180px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.3)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '9s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'rgba(186, 230, 253, 0.4)',
            bottom: '-50px',
            right: '20%',
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '12s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'rgba(224, 242, 254, 0.5)',
            top: '70%',
            right: '10%',
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '15s',
          }}
        ></div>

        {/* Medium dots */}
        <div
          style={{
            position: 'absolute',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(30, 64, 175, 0.5)',
            top: '12%',
            left: '30%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            background: 'rgba(37, 99, 235, 0.45)',
            top: '35%',
            left: '65%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '2s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '85px',
            height: '85px',
            borderRadius: '50%',
            background: 'rgba(59, 130, 246, 0.5)',
            top: '55%',
            left: '20%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '4s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '75px',
            height: '75px',
            borderRadius: '50%',
            background: 'rgba(96, 165, 250, 0.4)',
            top: '25%',
            left: '75%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '1s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '90px',
            height: '90px',
            borderRadius: '50%',
            background: 'rgba(30, 64, 175, 0.45)',
            top: '65%',
            left: '50%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '3s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '65px',
            height: '65px',
            borderRadius: '50%',
            background: 'rgba(37, 99, 235, 0.4)',
            top: '8%',
            left: '55%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '5s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(59, 130, 246, 0.45)',
            top: '45%',
            left: '10%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '1.5s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            background: 'rgba(96, 165, 250, 0.5)',
            top: '72%',
            left: '70%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '3.5s',
          }}
        ></div>

        {/* Small dots */}
        <div
          style={{
            position: 'absolute',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '15%',
            left: '20%',
            animation: 'floatDot 15s infinite ease-in-out',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '35px',
            height: '35px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '25%',
            left: '60%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '1s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '35%',
            left: '15%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '2s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '45px',
            height: '45px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '45%',
            left: '70%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '0.5s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '35px',
            height: '35px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '55%',
            left: '30%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '1.5s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '65%',
            left: '80%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '2.5s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '20%',
            left: '45%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '3s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '35px',
            height: '35px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '30%',
            left: '10%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '0.8s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '40%',
            left: '55%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '1.8s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '50%',
            left: '25%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '2.8s',
          }}
        ></div>
      </div>
      <div
        className='container justify-content-center align-items-center'
        style={{ padding: '20px 0', position: 'relative', zIndex: 1 }}
      >
        <div className='row'>
          <div className='col-md-8 col-lg-8' style={{ margin: '0 auto' }}>
            {/* Header Cards */}
            <div
              className='card shadow'
              style={{ background: 'linear-gradient(90deg, #3b82f6, #60a5fa)' }}
            >
              <div className='card-body text-center'>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                  <h4 className='card-title text-white flex-grow-1'>
                    Payment Portal
                  </h4>
                  <Button variant='outline-danger' onClick={handleLogout}>
                    <FaSignOutAlt className='me-1' /> Logout
                  </Button>
                </div>
                <div className='text-center mt-3'>
                  <div className='d-flex justify-content-center align-items-center'>
                    <span className='text-white'>
                      Welcome{' '}
                      {userInfo.name.replace(/^\w/, (c) => c.toUpperCase())},
                    </span>
                  </div>
                  <div className='d-flex justify-content-center align-items-center'>
                    <span className='text-white'>
                      {' '}
                      Branches:{' '}
                      {userInfo.branches && userInfo.branches.length > 0
                        ? userInfo.branches.join(', ')
                        : 'No branches assigned'}
                    </span>
                  </div>
                </div>
                <div className='row mt-3'>
                  <div className='col-md-4'>
                    <div className='box'>
                      <h3 className='text-white'>
                        {summary.pending_transfers || 0}
                      </h3>
                      <p className='text-white'>Pending Transfers</p>
                    </div>
                  </div>
                  <div className='col-md-4'>
                    <div className='box'>
                      <h3 className='text-white'>
                        {summary.total_requests || 0}
                      </h3>
                      <p className='text-white'>Total Requests</p>
                    </div>
                  </div>

                  <div className='col-md-4'>
                    <div className='box'>
                      <h3 className='text-white'>
                        {summary.active_drivers || 0}
                      </h3>
                      <p className='text-white'>Active Drivers</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Main Content */}
            <div className='card shadow' style={{ background: '#ffffff' }}>
              <div className='card-body'>
                {/* Buttons */}
                <div className='gap-3 mb-4'>
                  <div className='row'>
                    <div className='col-md-3'>
                      <Button
                        className={`for_btn ${activeForm === 'formA' ? 'active' : ''
                          }`}
                        variant='primary'
                        onClick={() => setActiveForm('formA')}
                      >
                        <i className='fa fa-plus'></i> New Request
                      </Button>
                    </div>
                    <div className='col-md-3'>
                      <Button
                        className={`for_btn ${activeForm === 'formB' ? 'active' : ''
                          }`}
                        variant='success'
                        onClick={() => setActiveForm('formB')}
                      >
                        <i className='fa fa-clock'></i> Verified
                      </Button>
                    </div>
                    <div className='col-md-3'>
                      <Button
                        className={`for_btn ${activeForm === 'formC' ? 'active' : ''
                          }`}
                        variant='warning'
                        onClick={() => setActiveForm('formC')}
                      >
                        <i className='fa fa-check-circle'></i> Collected
                      </Button>
                    </div>
                    <div className='col-md-3'>
                      <Button
                        className={`for_btn ${activeForm === 'formD' ? 'active' : ''
                          }`}
                        variant='danger'
                        onClick={() => setActiveForm('formD')}
                      >
                        <i className='fa fa-history'></i> History
                      </Button>
                    </div>
                  </div>
                </div>
                {/* FORM A */}
                {activeForm === 'formA' && (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className='mb-3'>
                      <Form.Label>Customer Name</Form.Label>
                      <Form.Select
                        name='customerId'
                        value={formData.customerId}
                        onChange={handleCustomerChange}
                        required
                      >
                        <option value=''>-- Select Customer --</option>
                        {customers
                          ?.slice()                // create a copy so original is not mutated
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className='mb-3'>
                      <Form.Label>Branch</Form.Label>
                      <Form.Select
                        name='branchId'
                        value={formData.branchId}
                        onChange={handleBranchChange}
                        required
                      >
                        <option value=''>-- Select Branch --</option>
                        {branches.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className='mb-3'>
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        type='text'
                        name='address'
                        value={formData.address}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Form.Group className='mb-3'>
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type='text'
                        name='phone'
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className='mb-3'>
                      <Form.Label>Payment Method</Form.Label>
                      <Form.Select
                        name='method'
                        value={formData.method || ''}
                        onChange={handleChange}
                        required
                      >
                        <option value=''>-- Select Method --</option>
                        <option value='cash'>Cash</option>
                        <option value='cheque'>Cheque</option>
                        <option value='card_payment'>Card Payment</option>
                        <option value='bank_transfer'>Bank Transfer</option>
                        <option value='other'>Other</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className='mb-3'>
                      <Form.Label>Amount (AED)</Form.Label>
                      <Form.Control
                        type='number'
                        name='amount'
                        value={formData.amount}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className='mb-3'>
                      <Form.Label>Attachment (PDF)</Form.Label>
                      <Form.Control
                        type='file'
                        name='attachment'
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Form.Group className='mb-3'>
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as='textarea'
                        rows={3}
                        name='notes'
                        value={formData.notes}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Button
                      type='submit'
                      variant='success'
                      style={{ width: '100%' }}
                    >
                      Submit Request
                    </Button>
                  </Form>
                )}
                {/* FORM B - Verified */}
                {activeForm === 'formB' && (
                  <>
                    {verifiedPayments.length === 0 ? (
                      <div className='text-center py-5'>
                        <p className='text-muted' style={{ fontSize: '18px' }}>
                          No data available
                        </p>
                      </div>
                    ) : (
                      verifiedPayments.map((req) => (
                        <Card
                          key={req.id}
                          className='mb-3'
                          style={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'box-shadow 0.3s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow =
                              '0 4px 8px rgba(0,0,0,0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow =
                              '0 2px 4px rgba(0,0,0,0.1)';
                          }}
                        >
                          <Card.Body>
                            <div className='row'>
                              <div className='col-md-8'>
                                <div className='d-flex align-items-center mb-2'>
                                  <h5
                                    className='mb-0 me-2'
                                    style={{
                                      fontSize: '1.1rem',
                                      fontWeight: '600',
                                    }}
                                  >
                                    Request #{req.id} - {req.uuid}
                                  </h5>
                                  <Badge bg='success'>{req.status}</Badge>
                                </div>
                                <div className='row mt-2'>
                                  <div className='col-md-6'>
                                    <p
                                      className='mb-2'
                                      style={{ fontSize: '0.9rem' }}
                                    >
                                      <strong>Customer:</strong>{' '}
                                      {req.customerName}
                                    </p>
                                    <p
                                      className='mb-2'
                                      style={{ fontSize: '0.9rem' }}
                                    >
                                      <strong>Amount:</strong> AED {req.amount}
                                    </p>
                                  </div>
                                  <div className='col-md-6'>
                                    <p
                                      className='mb-2'
                                      style={{ fontSize: '0.9rem' }}
                                    >
                                      <strong>Branch:</strong> {req.branchName}
                                    </p>
                                    <p
                                      className='mb-2'
                                      style={{ fontSize: '0.9rem' }}
                                    >
                                      <strong>Collected By:</strong>{' '}
                                      {req.driverName || 'N/A'}
                                    </p>
                                  </div>
                                </div>
                                {req.method && (
                                  <p
                                    className='mb-0'
                                    style={{ fontSize: '0.9rem' }}
                                  >
                                    <strong>Method:</strong> {req.method}
                                  </p>
                                )}
                              </div>
                              <div className='col-md-4 d-flex align-items-center justify-content-end'>
                                <Button
                                  variant='primary'
                                  onClick={() => handleOpenModal(req, 'view')}
                                  style={{
                                    minWidth: '100px',
                                    fontSize: '0.9rem',
                                  }}
                                >
                                  View Details
                                </Button>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    )}
                  </>
                )}
                {/* FORM C - Collected */}
                {activeForm === 'formC' && (
                  <>
                    {collectedPayments.length === 0 ? (
                      <div className='text-center py-5'>
                        <p className='text-muted' style={{ fontSize: '18px' }}>
                          No data available
                        </p>
                      </div>
                    ) : (
                      collectedPayments.map((req) => (
                        <Card key={req.id} className='mb-3'>
                          <Card.Body className='d-flex justify-content-between align-items-center'>
                            <div style={{ lineHeight: '1.2' }}>
                              <h5
                                className='mb-2 me-2'
                                style={{
                                  fontSize: '1.1rem',
                                  fontWeight: '600',
                                }}>
                                Request #{req.id} - {req.uuid}
                              </h5>
                              <p style={{ marginBottom: '8px', fontSize: '0.9rem' }}>
                                <strong>Customer:</strong>{' '} {req.customerName}
                              </p>
                              <p style={{ marginBottom: '8px', fontSize: '0.9rem' }}>
                                <strong>Amount:</strong>{' '} AED {req.amount}
                              </p>
                              <p style={{ marginBottom: '8px', fontSize: '0.9rem' }}>
                                <strong>Collected By:</strong>{' '} {req.driverName || 'N/A'}
                              </p>
                              <p style={{ marginBottom: '8px', fontSize: '0.9rem' }}>
                                <strong>Status:</strong>{' '}<Badge bg='success'>{req.status}</Badge>
                              </p>
                              <p style={{ marginBottom: '0', fontSize: '0.9rem' }}>
                                <strong>Method:</strong>{' '} {req.method || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <Button
                                variant='info'
                                onClick={() => handleOpenModal(req, 'view')}
                              >
                                View
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    )}
                  </>
                )}
                {/* FORM D - History */}
                {activeForm === 'formD' && (
                  <>
                    <div className='d-flex gap-2 mb-3 align-items-end'>
                      <div classname='mt-2'>
                        <Form.Label>From</Form.Label>
                      </div>
                      <Form.Control
                        type='date'
                        name='from'
                        value={historyFilter.from}
                        onChange={handleHistoryDateChange}
                        style={{ width: '150px' }}
                      />

                      <div className='mt-2'>
                        <Form.Label>To</Form.Label>
                      </div>
                      <Form.Control
                        type='date'
                        name='to'
                        value={historyFilter.to}
                        onChange={handleHistoryDateChange}
                        style={{ width: '150px' }}
                      />
                      <Button
                        variant='primary'
                        size='sm'
                        onClick={() => {
                          // Fetch payments with selected date filters
                          fetchHistory();
                        }}
                        style={{ height: '38px', marginTop: '24px' }}
                      >
                        <FaSearch className='me-1' />
                        Filter
                      </Button>
                      <Button
                        variant='outline-secondary'
                        size='sm'
                        onClick={() => {
                          const now = new Date();
                          const year = now.getFullYear();
                          const month = now.getMonth() + 1;
                          const firstDay = `${year}-${String(month).padStart(
                            2,
                            '0'
                          )}-01`;
                          const lastDay = new Date(year, month, 0).getDate();
                          const lastDayStr = `${year}-${String(month).padStart(
                            2,
                            '0'
                          )}-${String(lastDay).padStart(2, '0')}`;

                          setHistoryFilter({
                            from: firstDay,
                            to: lastDayStr,
                          });
                          // Fetch with default filters (YYYY-MM-DD format)
                          fetchHistory(firstDay, lastDayStr);
                        }}
                        style={{ height: '38px', marginTop: '24px' }}
                      >
                        Clear
                      </Button>
                    </div>

                    {historyData.length === 0 ? (
                      <div className='text-center py-5'>
                        <p className='text-muted' style={{ fontSize: '18px' }}>
                          No data available
                        </p>
                      </div>
                    ) : (
                      historyData.map((req) => (
                        <Card key={req.id} className='mb-3'>
                          <Card.Body className='d-flex justify-content-between align-items-start'>
                            <div style={{ lineHeight: '1.2' }}>
                              <h5
                                className='mb-2 me-2'
                                style={{
                                  fontSize: '1.1rem',
                                  fontWeight: '600',
                                }}
                              >
                                Request #{req.uuid}
                                <Badge bg='secondary' className='ms-2'>
                                  {req.type}
                                </Badge>
                              </h5>
                              <p
                                style={{
                                  marginBottom: '8px',
                                  fontSize: '0.9rem',
                                }}
                              >
                                <strong>Customer:</strong> {req.customerName}
                              </p>
                              <p
                                style={{
                                  marginBottom: '8px',
                                  fontSize: '0.9rem',
                                }}
                              >
                                <strong>Amount:</strong> AED {req.amount}
                              </p>
                              <p
                                style={{
                                  marginBottom: '8px',
                                  fontSize: '0.9rem',
                                }}
                              >
                                <strong>Status:</strong>{' '}
                                <Badge bg={getStatusVariant(req.status)}>
                                  {req.status}
                                </Badge>
                              </p>
                              <p style={{ marginBottom: '0' }}>
                                Method: {req.method || '—'}
                              </p>
                            </div>
                            <div className='d-flex flex-column gap-2'>
                              <Button
                                variant='info'
                                onClick={() => handleOpenModal(req, 'view')}
                              >
                                View
                              </Button>
                              {req.type === 'Payment' &&
                                req.status?.toLowerCase() === 'submitted' && (
                                <Button
                                  variant='success'
                                  onClick={() => handleOpenModal(req, 'assign')}
                                >
                                  Approve
                                </Button>
                              )}
                              {['created', 'assigned', 'assign_to_driver', 'assigned to driver'].includes(req.status?.toLowerCase()) && (
                                <Button
                                  variant='danger'
                                  onClick={() => handleDeleteItem(req.id, req.type)}
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    )}
                  </>
                )}
                <Modal show={show} onHide={handleClose} centered>
                  <Modal.Header closeButton>
                    {' '}
                    <Modal.Title>
                      {' '}
                      {modalType === 'assign'
                        ? 'Approve Request'
                        : 'Request Details'}
                      {' '}
                    </Modal.Title>
                    {' '}
                  </Modal.Header>
                  {' '}
                  <Modal.Body>
                    {' '}
                    {selectedRequest && modalType === 'view' && (
                      <div>
                        <Card
                          className='mb-0'
                          style={{
                            backgroundColor: '#ffffff',
                            border: 'none',
                            boxShadow: 'none',
                          }}
                        >
                          <Card.Body style={{ padding: '0' }}>
                            {/* Header Section */}
                            <div
                              style={{
                                background:
                                  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                padding: '20px',
                                borderRadius: '8px 8px 0 0',
                                marginBottom: '20px',
                              }}
                            >
                              <div className='d-flex justify-content-between align-items-center'>
                                <div>
                                  <h5
                                    className='text-white mb-1'
                                    style={{
                                      fontSize: '0.95rem',
                                      fontWeight: '600',
                                    }}
                                  >
                                    Request #{selectedRequest.id} -{' '}
                                    {selectedRequest.uuid}
                                  </h5>
                                  <Badge
                                    bg='light'
                                    text='dark'
                                    style={{ fontSize: '0.75rem' }}
                                  >
                                    {capitalizeFirstLetter(
                                      selectedRequest.status
                                    )}
                                  </Badge>
                                </div>
                                <div className='text-end'>
                                  <div
                                    style={{
                                      fontSize: '20px',
                                      fontWeight: 'bold',
                                      color: '#fff',
                                    }}
                                  >
                                    AED {selectedRequest.amount}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.75rem',
                                      color: 'rgba(255,255,255,0.9)',
                                    }}
                                  >
                                    Payment Amount
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Details Section */}
                            <div className='row' style={{ padding: '0 5px' }}>
                              <div className='col-md-6 mb-3'>
                                <div
                                  style={{
                                    padding: '12px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: '0.7rem',
                                      color: '#6c757d',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      marginBottom: '4px',
                                    }}
                                  >
                                    Customer
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      color: '#212529',
                                    }}
                                  >
                                    {selectedRequest.customerName}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: '12px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: '0.7rem',
                                      color: '#6c757d',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      marginBottom: '4px',
                                    }}
                                  >
                                    Branch
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      color: '#212529',
                                    }}
                                  >
                                    {selectedRequest.branchName}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: '12px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: '0.7rem',
                                      color: '#6c757d',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      marginBottom: '4px',
                                    }}
                                  >
                                    Collected By
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      color: '#212529',
                                    }}
                                  >
                                    {selectedRequest.driverName || 'N/A'}
                                  </div>
                                </div>
                              </div>
                              <div className='col-md-6 mb-3'>
                                <div
                                  style={{
                                    padding: '12px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: '0.7rem',
                                      color: '#6c757d',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      marginBottom: '4px',
                                    }}
                                  >
                                    Payment Method
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      color: '#212529',
                                    }}
                                  >
                                    {selectedRequest.method
                                      ? selectedRequest.method
                                        .split('_')
                                        .map((word) =>
                                          capitalizeFirstLetter(word)
                                        )
                                        .join(' ')
                                      : 'N/A'}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: '12px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: '0.7rem',
                                      color: '#6c757d',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      marginBottom: '4px',
                                    }}
                                  >
                                    Created At
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      color: '#212529',
                                    }}
                                  >
                                    {selectedRequest.created_at
                                      ? new Date(
                                        selectedRequest.created_at
                                      ).toLocaleString()
                                      : 'N/A'}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Notes Section */}
                            {selectedRequest.notes && (
                              <div
                                style={{
                                  padding: '0 5px',
                                  marginTop: '15px',
                                  marginBottom: '15px',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: '0.7rem',
                                    color: '#6c757d',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    marginBottom: '8px',
                                  }}
                                >
                                  Notes
                                </div>
                                <div
                                  style={{
                                    padding: '15px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    border: '1px solid #e9ecef',
                                    fontSize: '0.8rem',
                                    color: '#495057',
                                    lineHeight: '1.5',
                                  }}
                                >
                                  {selectedRequest.notes}
                                </div>
                              </div>
                            )}

                            {/* Attachments Section */}
                            <div
                              style={{
                                padding: '0 5px',
                                marginTop: '15px',
                                paddingBottom: '10px',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '0.7rem',
                                  color: '#6c757d',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  marginBottom: '10px',
                                }}
                              >
                                Attachments
                              </div>
                              {selectedRequest.attachments &&
                                selectedRequest.attachments.length > 0 ? (
                                <div className='d-flex flex-wrap gap-2'>
                                  {selectedRequest.attachments.map((file) => (
                                    <a
                                      key={file.id}
                                      href={`${BACKEND_URL}/${file.path}`}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='btn btn-outline-primary btn-sm'
                                      style={{
                                        textDecoration: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        padding: '5px 10px',
                                      }}
                                    >
                                      <FaBoxOpen className='me-1' />{' '}
                                      {file.type || 'Attachment'}
                                    </a>
                                  ))}
                                </div>
                              ) : (
                                <div
                                  style={{
                                    padding: '10px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    color: '#6c757d',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  No attachments available
                                </div>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    )}
                    {selectedRequest && modalType === 'assign' && (
                      <Form>
                        {' '}
                        <Form.Group className='mb-3'>
                          {' '}
                          <Form.Label>
                            <b>Driver</b>
                          </Form.Label>
                          {' '}
                          <Form.Select
                            value={selectedRequest.driverName || ''}
                            disabled
                          >
                            {' '}
                            <option value={selectedRequest.driverName || ''}>
                              {selectedRequest.driverName || '—'}
                              {' '}
                            </option>
                            {' '}
                          </Form.Select>
                          {' '}
                        </Form.Group>
                        {' '}
                        <Form.Group className='mb-3'>
                          {' '}
                          <Form.Label>
                            <b>Payment Method</b>
                          </Form.Label>
                          {' '}
                          <Form.Select
                            value={selectedRequest.method || ''}
                            disabled
                          >
                            {' '}
                            <option value={selectedRequest.method || ''}>
                              {selectedRequest.method || '—'}       {' '}
                            </option>
                            {' '}
                          </Form.Select>
                          {' '}
                        </Form.Group>
                        {' '}
                      </Form>
                    )}
                    {' '}
                  </Modal.Body>
                  {' '}
                  <Modal.Footer>
                    {' '}
                    <Button variant='secondary' onClick={handleClose}>
                      Cancel
                    </Button>
                    {modalType === 'assign' && // This is the "Approve" flow
                      selectedRequest?.status?.toLowerCase() ===
                      'collected' && (
                        <Button
                          variant='success'
                          onClick={handleApproveRequest}
                        >
                          Confirm
                        </Button>
                      )}{' '}
                  </Modal.Footer>
                </Modal>
                {' '}
              </div>
              {' '}
            </div>
            {' '}
          </div>
          {' '}
        </div>
        {' '}
      </div>
      {' '}
    </div>
  );
};

export default PaymentPortal;
